import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import styled from "styled-components";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  deleteNode,
  updateNodeLabel,
  updateAddNodes,
  updateNodeWidth,
  updateNodeHeight,
  resetNodesState,
  updatePlotWidth,
  updatePlotHeight
} from "../../../redux/features/nodes";
import { toggleUnit } from "../../../redux/features/appState";
import { Nodes } from "../../../redux/features/nodes/nodes.model";
import { showToast } from "../../../redux/features/toast/toast.slice";

const CustomDiv = styled.div`
  .container {
    width: 100%;
    height: calc(100vh - 170px);
    display: flex;
    flex-direction: column;
    background: #FBE5C6;
    font-family: Poppins;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .header {
    font-size: 18px;
    font-weight: 500;
    border-radius: 8px 8px 0 0;
    color: #FBE5C6;
    background-color: #FF9A56;
    padding: 16px;
    text-align: center;
  }

  .scroll-container {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  .grid {
    display: grid;
    grid-template-columns: 36px 2fr 1.1fr 1.1fr;
    align-items: center;
    gap: 8px;
  }

  .row {
    padding: 8px 0;
    border-bottom: 1px solid #eee;
  }

  .header-row {
    font-weight: 600;
    padding-bottom: 6px;
  }

  .empty-message {
    text-align: center;
    margin-top: 50px;
    color: #555;
  }
`;

const FT_TO_M = 0.3048;
const M_TO_FT = 3.28084;
const RADIUS = 65;

const LeftSidebar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const nodes = useSelector((state: RootState) => state.nodesState.nodes);
  const unit = useSelector(
    (state: RootState) => state.outputEditorButtonState.unit
  );

  const [open, setOpen] = useState(false);
  const [customRoomOpen, setCustomRoomOpen] = useState(false);
  const [customRoomName, setCustomRoomName] = useState("");
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  useEffect(() => {
    dispatch(updatePlotWidth(0));
    dispatch(updatePlotHeight(0));
  }, []);



  const toDisplay = (val: number) =>
    unit === "meters" ? Math.round(val * FT_TO_M) : val;

  const toFeet = (val: number) =>
    unit === "meters" ? Math.round(val * M_TO_FT) : val;

  const handleDeleteNode = (id: number) => dispatch(deleteNode(id));

  const handleLabelChange = (id: number, label: string) =>
    dispatch(updateNodeLabel({ id, label }));

  const handleToggleUnit = () => dispatch(toggleUnit());

  const handleViewSavedDocuments = () => {
    navigate("/saved-documents");
  };

  const getDefaultRoomDimensions = (roomtype: string) => {
    switch (roomtype.toLowerCase()) {
      case "bedroom":
        return {
          width: { min: 10, max: 18 },
          height: { min: 9, max: 16 },
          ratio: { min: 1, max: 2 },
        };
      case "living room":
        return {
          width: { min: 12, max: 24 },
          height: { min: 10, max: 20 },
          ratio: { min: 1, max: 2 },
        };
      case "kitchen":
        return {
          width: { min: 8, max: 16 },
          height: { min: 6, max: 14 },
          ratio: { min: 1, max: 2 },
        };
      case "bathroom":
        return {
          width: { min: 4, max: 10 },
          height: { min: 6, max: 10 },
          ratio: { min: 1, max: 2 },
        };
      case "office":
        return {
          width: { min: 6, max: 16 },
          height: { min: 6, max: 14 },
          ratio: { min: 1, max: 2 },
        };
      case "dining room":
        return {
          width: { min: 6, max: 16 },
          height: { min: 7, max: 14 },
          ratio: { min: 1, max: 2 },
        };
      default:
        return {
          width: { min: 10, max: 15 },
          height: { min: 10, max: 15 },
          ratio: { min: 1, max: 2 },
        };
    }
  };

  const generateRandomPosition = () => {
    const width = visualViewport?.width ?? 1200;
    const height = visualViewport?.height ?? 800;
    const stripHeight = 180;

    const minX = width * 0.25;
    const maxX = width * 0.75;
    const minY = height * 0.25;
    const maxY = (height - stripHeight) * 0.85;

    const minDistance = 2 * RADIUS;

    let x = 0;
    let y = 0;
    let attempts = 0;
    const maxAttempts = 150;

    do {
      x = minX + Math.random() * (maxX - minX);
      y = minY + Math.random() * (maxY - minY);
      attempts++;
      const overlapping = hasOverlapWithNodes(x, y, minDistance);
      if (!overlapping) break;
    } while (attempts < maxAttempts);

    return { x, y };
  };

  const handleOpenAddDialog = () => setOpen(true);
  const handleCloseAddDialog = () => setOpen(false);

  const handleOpenCustomRoomDialog = () => {
    setCustomRoomName("");
    setCustomRoomOpen(true);
  };

  const handleCloseCustomRoomDialog = () => {
    setCustomRoomOpen(false);
    setCustomRoomName("");
  };

  const getNextRoomLabel = (baseName: string) => {
    const count = nodes.filter((n) =>
      n.label.toLowerCase().startsWith(baseName.toLowerCase())
    ).length;
    return `${baseName} ${count + 1}`;
  };

  const hasOverlapWithNodes = (
    x: number,
    y: number,
    minDistance: number
  ): boolean => {
    for (const node of nodes) {
      if (Math.sqrt((node.x - x) ** 2 + (node.y - y) ** 2) < minDistance) {
        return true;
      }
    }
    return false;
  };

  const hasOverlapWithPositions = (
    positions: { x: number; y: number }[],
    x: number,
    y: number,
    minDistance: number
  ): boolean => {
    for (const pos of positions) {
      if (Math.sqrt((pos.x - x) ** 2 + (pos.y - y) ** 2) < minDistance) {
        return true;
      }
    }
    return false;
  };

  const handleToggleRoomSelection = (roomtype: string) => {
    if (roomtype === "Others") {
      handleCloseAddDialog();
      return handleOpenCustomRoomDialog();
    }
    setSelectedRooms((prev) =>
      prev.includes(roomtype)
        ? prev.filter((r) => r !== roomtype)
        : [...prev, roomtype]
    );
  };

  const handleAddSelectedRooms = () => {
    const newPositions: { x: number; y: number }[] = [];

    selectedRooms.forEach((roomtype, index) => {
      const newLabel = getNextRoomLabel(roomtype);
      const dimensions = getDefaultRoomDimensions(roomtype);

      // Generate position avoiding both existing nodes AND newly added positions
      const width = visualViewport?.width ?? 1200;
      const height = visualViewport?.height ?? 800;
      const minX = width * 0.25;
      const maxX = width * 0.75;
      const minY = height * 0.25;
      const maxY = height * 0.75;
      const minDistance = 2 * RADIUS;

      let x = 0;
      let y = 0;
      let attempts = 0;
      const stripHeight = 180;

      do {
        x = width * 0.25 + Math.random() * (width * 0.5);
        y = height * 0.1 + Math.random() * ((height - stripHeight) * 0.75);
        attempts++;

        // Check against existing nodes in Redux and newly-added batch positions.
        const overlapsExisting = hasOverlapWithNodes(x, y, minDistance);
        const overlapsNew = hasOverlapWithPositions(
          newPositions,
          x,
          y,
          minDistance
        );

        if (!overlapsExisting && !overlapsNew) break;
      } while (attempts < 200);

      newPositions.push({ x, y });

      dispatch(
        updateAddNodes({
          id: nodes.length + index,
          label: newLabel,
          x,
          y,
          color: "#FF9A56",
          roomtype: roomtype,
          ...dimensions,
        })
      );
    });

    setSelectedRooms([]);
    handleCloseAddDialog();
  };

  const handleAddCustomRoom = () => {
    if (!customRoomName.trim()) return;

    const roomName = customRoomName.trim();

    // First add all previously selected rooms
    const allRooms = [...selectedRooms, roomName];
    const newPositions: { x: number; y: number }[] = [];

    allRooms.forEach((roomtype, index) => {
      const label = roomtype === roomName ? roomName : getNextRoomLabel(roomtype);
      const dimensions = getDefaultRoomDimensions(roomtype);

      const width = visualViewport?.width ?? 1200;
      const height = visualViewport?.height ?? 800;
      const minDistance = 2 * RADIUS;

      let x = 0;
      let y = 0;
      let attempts = 0;

      do {
        x = (width * 0.25) + Math.random() * (width * 0.5);
        y = (height * 0.25) + Math.random() * (height * 0.5);
        attempts++;
        const overlapsExisting = nodes.some(
          (node) => Math.sqrt((node.x - x) ** 2 + (node.y - y) ** 2) < minDistance
        );
        const overlapsNew = newPositions.some(
          (pos) => Math.sqrt((pos.x - x) ** 2 + (pos.y - y) ** 2) < minDistance
        );
        if (!overlapsExisting && !overlapsNew) break;
      } while (attempts < 200);

      newPositions.push({ x, y });

      dispatch(
        updateAddNodes({
          id: nodes.length + index,
          label,
          roomtype,
          x,
          y,
          color: "#FF9A56",
          ...dimensions,
        })
      );
    });

    setSelectedRooms([]);
    handleCloseCustomRoomDialog();
  };

  const handleWidthChange = (id: number, value: string) => {
    let cleaned = value.replace(/[^0-9]/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.replace(/^0+/, "");
    }

    if (cleaned === "") {
      dispatch(showToast({ message: "Dimension cannot be empty or null", type: "error" }));
      dispatch(updateNodeWidth({ id, min: 0, max: 0 }));
      return;
    }

    const numValue = parseInt(cleaned, 10);
    if (!isNaN(numValue) && numValue > 0) {
      const feetValue = toFeet(numValue);
      dispatch(updateNodeWidth({ id, min: feetValue, max: feetValue }));
    }
  };

  const handleHeightChange = (id: number, value: string) => {
    let cleaned = value.replace(/[^0-9]/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.replace(/^0+/, "");
    }

    if (cleaned === "") {
      dispatch(showToast({ message: "Dimension cannot be empty or null", type: "error" }));
      dispatch(updateNodeHeight({ id, min: 0, max: 0 }));
      return;
    }

    const numValue = parseInt(cleaned, 10);
    if (!isNaN(numValue) && numValue > 0) {
      const feetValue = toFeet(numValue);
      dispatch(updateNodeHeight({ id, min: feetValue, max: feetValue }));
    }
  };

  return (
    <Box sx={{ width: "20vw", height: "calc(100vh - 170px)", margin: "8px 0px 0px 8px" }}>
      <CustomDiv>
        <Box className="container">
          <Typography className="header">Room Dimensions</Typography>

          <div className="scroll-container">
            {nodes.length === 0 ? (
              <div className="empty-message">
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", color: "red", fontSize: 20, mt: -7 }}
                >
                  You currently have 0 rooms
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, fontWeight: "600", color: "black", fontSize: 15 }}
                >
                  Click on Add Room to create a room
                </Typography>
              </div>
            ) : (
              <>
                <div className="grid header-row">
                  <div />
                  <div>Room</div>
                  <div>Width</div>
                  <div>Height</div>
                </div>

                {nodes.map((node: Nodes) => (
                  <div className="grid row" key={node.id}>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDeleteNode(node.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>

                    <TextField
                      size="small"
                      value={node.label}
                      onChange={(e) => handleLabelChange(node.id, e.target.value)}
                    />

                    <TextField
                      size="small"
                      value={node.width.min === 0 ? "" : toDisplay(node.width.min)}
                      onChange={(e) => handleWidthChange(node.id, e.target.value)}
                      inputProps={{
                        type: "text",
                        inputMode: "numeric",
                        pattern: "[0-9]*"
                      }}
                    />

                    <TextField
                      size="small"
                      value={node.height.min === 0 ? "" : toDisplay(node.height.min)}
                      onChange={(e) => handleHeightChange(node.id, e.target.value)}
                      inputProps={{
                        type: "text",
                        inputMode: "numeric",
                        pattern: "[0-9]*"
                      }}
                    />
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 1,
              borderTop: "1px solid #eee",
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{
                backgroundColor: "#FF9A56",
                textTransform: "none",
                fontWeight: 500,
                fontFamily: "Poppins",
              }}
            >
              Add Room
            </Button>

            <Box sx={{ display: "flex" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "5px",
                  borderRadius: "0 0 0 8px",
                  border: "2px solid #FF9A56",
                  flex: 1,
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    color: unit === "feet" ? "#FF9A56" : "#333333",
                    fontSize: "14px",
                    fontWeight: 600,
                    fontFamily: "Poppins",
                  }}
                >
                  Feet
                </Typography>
                <Switch
                  checked={unit === "meters"}
                  onChange={handleToggleUnit}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#FF9A56",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#FF9A56",
                    },
                  }}
                />
                <Typography
                  sx={{
                    color: unit === "meters" ? "#FF9A56" : "#333333",
                    fontSize: "14px",
                    fontWeight: 600,
                    fontFamily: "Poppins",
                  }}
                >
                  Meters
                </Typography>
              </Box>

              <Button
                variant="outlined"
                sx={{
                  border: "2px solid #ED4337",
                  color: "#ED4337",
                  padding: "5px 20px",
                  borderRadius: "0 0 8px 0",
                  backgroundColor: "#FDEDEC",
                  fontFamily: "Poppins",
                  "&:hover": {
                    backgroundColor: "#F9CACA",
                  },
                }}
                onClick={() => {
                  dispatch(resetNodesState());
                  setSelectedRooms([]);
                }}
              >
                Reset
              </Button>
            </Box>

            <Button
              variant="outlined"
              onClick={handleViewSavedDocuments}
              sx={{
                border: "2px solid #FF9A56",
                color: "#FF9A56",
                textTransform: "none",
                fontWeight: 500,
                fontFamily: "Poppins",
                "&:hover": {
                  backgroundColor: "#FFE4CC",
                },
              }}
            >
              View Saved Documents
            </Button>
          </Box>
        </Box>
      </CustomDiv>

      {/* Main Dialog */}
      <Dialog open={open} onClose={() => { setSelectedRooms([]); handleCloseAddDialog(); }}>
        <DialogTitle>Select Room Types</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            minWidth: "300px",
            paddingTop: "10px",
          }}
        >
          {["Bathroom", "Kitchen", "Living Room", "Bedroom", "Office", "Dining Room", "Others"].map((room) => (
            <Button
              key={room}
              variant={selectedRooms.includes(room) ? "contained" : "outlined"}
              onClick={() => handleToggleRoomSelection(room)}
              sx={{
                backgroundColor: selectedRooms.includes(room) ? "#FF9A56" : "transparent",
                color: selectedRooms.includes(room) ? "#FBE5C6" : "#FF9A56",
                border: "2px solid #FF9A56",
                textTransform: "none",
                fontFamily: "Poppins",
                "&:hover": {
                  backgroundColor: selectedRooms.includes(room) ? "#163a6b" : "#e8f0fe",
                },
              }}
            >
              {room} {selectedRooms.includes(room) ? "✓" : ""}
            </Button>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelectedRooms([]); handleCloseAddDialog(); }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedRooms}
            variant="contained"
            disabled={selectedRooms.length === 0}
            sx={{ backgroundColor: "#FF9A56", fontFamily: "Poppins" }}
          >
            Add {selectedRooms.length > 0 ? `(${selectedRooms.length})` : ""} Rooms
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Room Dialog */}
      <Dialog open={customRoomOpen} onClose={handleCloseCustomRoomDialog}>
        <DialogTitle>Enter Room Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={customRoomName}
            onChange={(e) => setCustomRoomName(e.target.value)}
            placeholder="Room Name"
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddCustomRoom();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomRoomDialog}>Cancel</Button>
          <Button
            onClick={handleAddCustomRoom}
            variant="contained"
            sx={{ backgroundColor: "#FF9A56" }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeftSidebar;